/* ============================================================
   Video Slider — slider carousel + video play behavior
   Vanilla JS, Dawn-style custom elements, no dependencies.
   ============================================================ */

class VideoSliderCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.slides = [];
    this.prevBtn = null;
    this.nextBtn = null;
    this.currentIndex = 0;
    this.slidesPerView = 1;
    this.slideWidth = 0;
    this.gap = 0;
    this._onResize = this._onResize.bind(this);
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));
    this.prevBtn = this.querySelector('[data-direction="prev"]');
    this.nextBtn = this.querySelector('[data-direction="next"]');

    if (!this.track || this.slides.length === 0) return;

    this.prevBtn?.addEventListener('click', this._onPrev);
    this.nextBtn?.addEventListener('click', this._onNext);

    // Swipe / drag support
    this.track.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.track.addEventListener('touchmove', this._onTouchMove, { passive: true });
    this.track.addEventListener('touchend', this._onTouchEnd);

    window.addEventListener('resize', this._onResize);

    // Hook up video play buttons per slide
    this._bindVideoPlayButtons();

    this._update();
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this._onPrev);
    this.nextBtn?.removeEventListener('click', this._onNext);
    this.track?.removeEventListener('touchstart', this._onTouchStart);
    this.track?.removeEventListener('touchmove', this._onTouchMove);
    this.track?.removeEventListener('touchend', this._onTouchEnd);
    window.removeEventListener('resize', this._onResize);
  }

  _bindVideoPlayButtons() {
    this.querySelectorAll('[data-play-video]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const mediaEl = btn.closest('video-slider-item');
        if (!mediaEl) return;
        const wrapper = mediaEl.querySelector('[data-video-wrapper]');
        const nativeVideo = mediaEl.querySelector('[data-video]');
        const iframe = mediaEl.querySelector('[data-video-iframe]');
        if (!wrapper) return;

        wrapper.hidden = false;

        if (nativeVideo) {
          nativeVideo.play().catch(() => {
            // autoplay can fail on some browsers; controls allow manual play
          });
        } else if (iframe && !iframe.src) {
          // Defer iframe src until user clicks play (perf + autoplay respect)
          iframe.src = iframe.dataset.videoSrc;
        }
      });
    });
  }

  _getResponsiveSlidesPerView() {
    const w = window.innerWidth;
    if (w <= 749) return parseInt(this.dataset.slidesMobile, 10) || 2;
    if (w <= 989) return parseInt(this.dataset.slidesTablet, 10) || 3;
    return parseInt(this.dataset.slidesDesktop, 10) || 5;
  }

  _measure() {
    this.slidesPerView = this._getResponsiveSlidesPerView();
    if (this.slides.length === 0) return;

    const firstSlide = this.slides[0];
    const rect = firstSlide.getBoundingClientRect();
    this.slideWidth = rect.width;

    // Read computed gap from the track
    const trackStyle = window.getComputedStyle(this.track);
    this.gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0;
  }

  _maxIndex() {
    return Math.max(0, this.slides.length - this.slidesPerView);
  }

  _update() {
    this._measure();

    // Clamp current index to valid range
    if (this.currentIndex > this._maxIndex()) {
      this.currentIndex = this._maxIndex();
    }
    if (this.currentIndex < 0) this.currentIndex = 0;

    const offset = -1 * this.currentIndex * (this.slideWidth + this.gap);
    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;

    // Toggle disabled state on arrows
    if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this._maxIndex();

    // Hide arrows if all slides already fit on screen
    const allVisible = this.slides.length <= this.slidesPerView;
    this.classList.toggle('video-slider__carousel-wrapper--no-nav', allVisible);
    if (this.prevBtn) this.prevBtn.style.visibility = allVisible ? 'hidden' : 'visible';
    if (this.nextBtn) this.nextBtn.style.visibility = allVisible ? 'hidden' : 'visible';
  }

  _onPrev() {
    this.currentIndex = Math.max(0, this.currentIndex - 1);
    this._update();
  }

  _onNext() {
    this.currentIndex = Math.min(this._maxIndex(), this.currentIndex + 1);
    this._update();
  }

  _onResize() {
    clearTimeout(this._resizeT);
    this._resizeT = setTimeout(() => this._update(), 120);
  }

  // --- Touch / swipe ---
  _onTouchStart(e) {
    this._touchStartX = e.touches[0].clientX;
    this._touchStartY = e.touches[0].clientY;
    this._touchMoved = false;
  }

  _onTouchMove(e) {
    if (this._touchStartX == null) return;
    const dx = e.touches[0].clientX - this._touchStartX;
    const dy = e.touches[0].clientY - this._touchStartY;
    if (Math.abs(dx) > Math.abs(dy)) this._touchMoved = true;
    this._touchDx = dx;
  }

  _onTouchEnd() {
    if (this._touchMoved && Math.abs(this._touchDx || 0) > 40) {
      if (this._touchDx < 0) this._onNext();
      else this._onPrev();
    }
    this._touchStartX = null;
    this._touchStartY = null;
    this._touchDx = 0;
  }
}

// Inner media container is a no-op custom element — used only as a stable
// querySelector target for the play-button binding above.
class VideoSliderItem extends HTMLElement {}

if (!customElements.get('video-slider-carousel')) {
  customElements.define('video-slider-carousel', VideoSliderCarousel);
}
if (!customElements.get('video-slider-item')) {
  customElements.define('video-slider-item', VideoSliderItem);
}

// Re-init support for the theme editor (when section is added / reordered)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const carousel = event.target.querySelector('video-slider-carousel');
    if (carousel && typeof carousel.connectedCallback === 'function') {
      // Already auto-upgraded by custom elements API; trigger a re-measure.
      setTimeout(() => carousel._update && carousel._update(), 50);
    }
  });
}





  (function() {
    function initDots(carousel) {
      const track = carousel.querySelector('[data-slider-track]');
      const dotsContainer = carousel.querySelector('[data-slider-dots]');
      const slides = carousel.querySelectorAll('[data-slider-slide]');
      const prevBtn = carousel.querySelector('[data-direction="prev"]');
      const nextBtn = carousel.querySelector('[data-direction="next"]');

      if (!track || !dotsContainer || !slides.length) return;

      function getSlidesPerView() {
        const w = window.innerWidth;
        if (w <= 749) return parseInt(carousel.dataset.slidesMobile, 10) || 2;
        if (w <= 989) return parseInt(carousel.dataset.slidesTablet, 10) || 3;
        return parseInt(carousel.dataset.slidesDesktop, 10) || 5;
      }

      function getCurrentIndex() {
        const slideWidth = slides[0].getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(track).gap) || 0;
        const transform = getComputedStyle(track).transform;
        let translateX = 0;
        if (transform && transform !== 'none') {
          const match = transform.match(/matrix.*\((.+)\)/);
          if (match) {
            const values = match[1].split(', ');
            translateX = Math.abs(parseFloat(values[4] || 0));
          }
        }
        return Math.round(translateX / (slideWidth + gap));
      }

      function buildDots() {
        const perView = getSlidesPerView();
        const pageCount = Math.max(1, Math.ceil(slides.length / perView));
        dotsContainer.innerHTML = '';

        for (let i = 0; i < pageCount; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'video-slider__dot';
          dot.setAttribute('aria-label', 'Go to slide group ' + (i + 1));
          dot.addEventListener('click', function() {
            const targetIndex = i * perView;
            scrollToIndex(targetIndex);
          });
          dotsContainer.appendChild(dot);
        }
        updateActiveDot();
      }

      function scrollToIndex(index) {
        const slideWidth = slides[0].getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(track).gap) || 0;
        const maxIndex = Math.max(0, slides.length - getSlidesPerView());
        const safeIndex = Math.min(index, maxIndex);
        track.style.transform = 'translateX(-' + (safeIndex * (slideWidth + gap)) + 'px)';
        setTimeout(updateActiveDot, 50);
      }

      function updateActiveDot() {
        const perView = getSlidesPerView();
        const currentIndex = getCurrentIndex();
        const activePage = Math.round(currentIndex / perView);
        const dots = dotsContainer.querySelectorAll('.video-slider__dot');
        dots.forEach(function(dot, i) {
          dot.classList.toggle('video-slider__dot--active', i === activePage);
        });
      }

      // Listen to existing arrow clicks to update active dot
      if (prevBtn) prevBtn.addEventListener('click', function() { setTimeout(updateActiveDot, 100); });
      if (nextBtn) nextBtn.addEventListener('click', function() { setTimeout(updateActiveDot, 100); });

      // Watch for transform changes on the track (catches any slide change)
      const observer = new MutationObserver(function() {
        setTimeout(updateActiveDot, 50);
      });
      observer.observe(track, { attributes: true, attributeFilter: ['style'] });

      // Rebuild dots on resize
      let resizeTimer;
      window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(buildDots, 200);
      });

      buildDots();
    }

    function initAll() {
      document.querySelectorAll('video-slider-carousel').forEach(initDots);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
  })();